<template>
    <v-container class="mb-12">
        <v-row justify="center">
            <v-col cols="12" md="8" lg="6">
                <v-card>
                    <v-card-title class="primary secondary--text d-block text-center">RECARGAR WALLET</v-card-title>
                    <v-card-text>
                        <v-alert dense text type="info" class="mt-3">
                            La recarga queda pendiente hasta que administracion verifique el comprobante.
                        </v-alert>
                        <v-form ref="form" v-model="valid" @submit.prevent="submit">
                            <v-select
                                v-model="form.currency"
                                :items="currencies"
                                item-text="code"
                                item-value="code"
                                label="Moneda"
                                :rules="[rules.required]"
                                @change="refreshAmountValidation"/>
                            <v-select
                                v-model="form.target_currency"
                                :items="walletCurrencies"
                                item-text="code"
                                item-value="code"
                                label="Moneda que recibes en wallet"
                                prepend-icon="mdi-wallet-plus"
                                :rules="[rules.required]"/>
                            <v-currency-field
                                ref="amountField"
                                v-model="form.amount"
                                label="Monto"
                                prepend-icon="mdi-cash-plus"
                                v-bind="currencyFieldConfig(form.currency)"
                                :rules="amountRules"
                                @input="refreshAmountValidation"/>
                            <v-alert dense text type="info" v-if="amountLimitText">
                                {{ amountLimitText }}
                            </v-alert>
                            <v-file-input
                                v-model="form.voucher"
                                label="Comprobante"
                                accept="image/png,image/jpeg,application/pdf"
                                prepend-icon="mdi-paperclip"
                                :rules="[rules.required]"/>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn text to="/"><v-icon left>mdi-arrow-left</v-icon> Volver</v-btn>
                        <v-spacer/>
                        <v-btn color="primary" :loading="loading" :disabled="loading || !valid" @click="submit">
                            <v-icon left>mdi-upload</v-icon> Enviar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';
import {
    currencyFieldConfig as moneyCurrencyFieldConfig,
    parseMoney
} from '../../helpers/money';

export default {
    name: "WalletRecharge",
    data: () => ({
        valid: true,
        loading: false,
        currencies: [],
        walletCurrencies: [],
        limits: {
            recharge: {
                minimums: {},
                maximums: {}
            },
            recharge_minimums: {}
        },
        form: {
            currency: 'USD',
            target_currency: 'USDC',
            amount: null,
            voucher: null
        },
        rules: {
            required: v => !!v || 'Campo obligatorio.'
        }
    }),
    created() {
        this.getWallet();
    },
    computed: {
        amountRules() {
            return [this.rules.required, this.amountLimitRule];
        },
        amountLimitText() {
            const minimum = this.amountLimit('min');
            const maximum = this.amountLimit('max');

            if (minimum && maximum) {
                return 'Monto permitido: ' + minimum.formatted + ' a ' + maximum.formatted + '.';
            }

            if (minimum) {
                return 'Monto minimo: ' + minimum.formatted + '.';
            }

            if (maximum) {
                return 'Monto maximo: ' + maximum.formatted + '.';
            }

            return '';
        }
    },
    methods: {
        getWallet() {
            axios.get('/api/wallet').then(res => {
                const rechargeOptions = res.data.quote_options && res.data.quote_options.recharge
                    ? res.data.quote_options.recharge
                    : [];
                const walletOptions = res.data.quote_options && res.data.quote_options.recharge_wallet_currencies
                    ? res.data.quote_options.recharge_wallet_currencies
                    : [];

                this.walletCurrencies = walletOptions.length ? walletOptions : (res.data.wallet_currencies || []);
                this.currencies = rechargeOptions.length ? rechargeOptions : this.fiatCurrencyItems(res.data.currencies || []);
                this.limits = res.data.limits || this.limits;

                if (this.currencies.length && !this.currencies.some(item => item.code === this.form.currency)) {
                    this.form.currency = this.currencies[0].code;
                }
                if (this.walletCurrencies.length && !this.walletCurrencies.some(item => item.code === this.form.target_currency)) {
                    this.form.target_currency = this.walletCurrencies[0].code;
                }
            });
        },
        fiatCurrencyItems(items) {
            const walletCodes = (this.walletCurrencies || []).map(item => String(item.code || '').toUpperCase());

            return (items || []).filter(item => {
                const code = String((typeof item === 'string' ? item : item.code) || '').toUpperCase();

                return walletCodes.indexOf(code) === -1;
            });
        },
        currencyFieldConfig(code) {
            return moneyCurrencyFieldConfig(this.currencies, code);
        },
        amountValue(value) {
            return parseMoney(value, this.currencies, this.form.currency);
        },
        amountLimit(kind) {
            const group = this.limits && this.limits.recharge ? this.limits.recharge : {};
            const key = kind === 'min' ? 'minimums' : 'maximums';
            const map = group[key] || {};

            if (kind === 'min') {
                return map[this.form.currency] || (this.limits.recharge_minimums ? this.limits.recharge_minimums[this.form.currency] : null) || null;
            }

            return map[this.form.currency] || null;
        },
        amountLimitError(value) {
            const amount = this.amountValue(value !== undefined ? value : this.form.amount);
            const minimum = this.amountLimit('min');
            const maximum = this.amountLimit('max');

            if (amount <= 0) {
                return '';
            }

            if (minimum && amount + 0.0001 < Number(minimum.amount)) {
                return 'El monto minimo es ' + minimum.formatted + '.';
            }

            if (maximum && amount - 0.0001 > Number(maximum.amount)) {
                return 'El monto maximo es ' + maximum.formatted + '.';
            }

            return '';
        },
        amountLimitRule(value) {
            return this.amountLimitError(value) || true;
        },
        refreshAmountValidation() {
            this.$nextTick(() => {
                const field = this.$refs.amountField;
                if (!field || typeof field.resetValidation !== 'function' || typeof field.validate !== 'function') {
                    return;
                }

                if (this.amountValue(this.form.amount) <= 0) {
                    field.resetValidation();
                    return;
                }

                field.validate(true);
            });
        },
        submit() {
            if (!this.$refs.form.validate()) return;
            this.loading = true;

            const data = new FormData();
            data.append('currency', this.form.currency);
            data.append('target_currency', this.form.target_currency);
            data.append('amount', this.amountValue(this.form.amount));
            data.append('voucher', this.form.voucher);

            axios.post('/api/wallet/recharge', data).then(res => {
                this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                this.$router.push('/');
            }).catch(err => {
                const msg = err.response && err.response.data && err.response.data.status ? err.response.data.status : 'No fue posible enviar la recarga.';
                this.$toast.error(msg, {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.loading = false;
            });
        }
    }
}
</script>
